import createClient from "openapi-fetch";
import type { components, paths } from "./schema";
import { getAccessToken } from "@/lib/auth/token-store";
import { refreshAccessToken } from "@/lib/auth/refresh";
import { recordHttpMetric } from "@/lib/observability/rum";

function toRequestUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === "string" && input.startsWith("/")) {
    return new URL(input, window.location.origin).toString();
  }
  return input;
}

export async function authorizedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const request = new Request(toRequestUrl(input), {
    ...init,
    credentials: "same-origin",
  });
  const startedAt = performance.now();
  const token = getAccessToken();
  if (token) request.headers.set("Authorization", `Bearer ${token}`);
  let response = await fetch(request);
  const isRefresh = new URL(
    request.url,
    window.location.origin,
  ).pathname.endsWith("/auth/refresh");
  if (response.status === 401 && !isRefresh) {
    const nextToken = await refreshAccessToken(token);
    if (nextToken) {
      const retry = new Request(request);
      retry.headers.set("Authorization", `Bearer ${nextToken}`);
      response = await fetch(retry);
    }
  }
  recordHttpMetric(request, response, performance.now() - startedAt);
  return response;
}

export async function uploadAvatar(
  file: Blob,
): Promise<components["schemas"]["AvatarView"]> {
  const form = new FormData();
  form.set("file", file, "avatar.png");
  const response = await authorizedFetch("/api/v1/profile/avatar", {
    method: "PUT",
    body: form,
  });
  if (!response.ok) throw await response.json();
  return (await response.json()) as components["schemas"]["AvatarView"];
}

export const api = createClient<paths>({
  baseUrl: "/api/v1",
  fetch: authorizedFetch,
});

export function normalizeApiError(error: unknown): {
  message: string;
  requestId?: string;
} {
  if (typeof error === "object" && error !== null) {
    const value = error as { detail?: unknown; request_id?: unknown };
    return {
      message:
        typeof value.detail === "string" ? value.detail : "Request failed",
      ...(typeof value.request_id === "string"
        ? { requestId: value.request_id }
        : {}),
    };
  }
  return { message: "Request failed" };
}
